import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ConfirmDialog,
  Input,
  PageHeader,
  RowMenu,
  Select,
  type SelectOption,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import type { LookupOption } from '../leads/types';
import { ContactFormModal } from './ContactFormModal';
import type { ContactPayload, ContactRow } from './types';

const PAGE_SIZE = 25;

export function ContactsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<ContactRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ContactRow | null>(null);

  const listQueryStr = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(offset));
    if (q.trim()) p.set('q', q.trim());
    if (categoryId) p.set('contact_category_id', categoryId);
    return p.toString();
  }, [q, categoryId, offset]);

  const contacts = useQuery({
    queryKey: ['contacts', listQueryStr],
    queryFn: () => apiRequest<ListResponse<ContactRow>>(`/contacts?${listQueryStr}`),
  });

  const categoriesQ = useQuery({
    queryKey: ['lookup', 'contact-categories'],
    queryFn: () =>
      apiRequest<ListResponse<LookupOption>>(
        '/settings/contact-categories?limit=100&include_inactive=false',
      ),
  });
  const categories = categoriesQ.data?.data ?? [];
  const catById = new Map(categories.map((c) => [c.id, c]));
  const categoryOpts: SelectOption[] = categories.map((c) => ({ value: c.id, label: c.name }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['contacts'] });

  const createMut = useMutation({
    mutationFn: (body: ContactPayload) =>
      apiRequest<{ data: ContactRow }>('/contacts', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: ContactPayload }) =>
      apiRequest<{ data: ContactRow }>(`/contacts/${args.id}`, {
        method: 'PATCH',
        body: args.body,
      }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/contacts/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const total = contacts.data?.pagination?.total ?? 0;
  const end = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Contacts"
        subtitle="Standalone people and organisations outside the lead/client pipeline."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New contact
          </Button>
        }
      />

      <div className="card" style={{ marginBottom: 12 }}>
        <div
          className="card-body"
          style={{ display: 'grid', gap: 10, gridTemplateColumns: '2fr 1fr' }}
        >
          <Input
            icon="search"
            placeholder="Search name, email, phone"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="All categories"
            value={categoryId}
            options={categoryOpts}
            onChange={(v) => {
              setCategoryId(v);
              setOffset(0);
            }}
          />
        </div>
      </div>

      {contacts.isLoading && <div className="card card-body muted">Loading…</div>}
      {contacts.error && (
        <div className="alert">
          {contacts.error instanceof Error ? contacts.error.message : 'Failed to load'}
        </div>
      )}
      {contacts.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Category</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {contacts.data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No contacts yet.
                  </td>
                </tr>
              )}
              {contacts.data.data.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/contacts/${c.id}`}>{c.full_name ?? 'Unnamed'}</Link>
                  </td>
                  <td className="cell-mono">{c.email ?? <span className="muted">—</span>}</td>
                  <td>{c.phone ?? <span className="muted">—</span>}</td>
                  <td>{c.city ?? <span className="muted">—</span>}</td>
                  <td>
                    {c.contact_category_id ? (
                      catById.get(c.contact_category_id)?.name ?? '—'
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <RowMenu
                      items={[
                        {
                          icon: 'arrowRight',
                          label: 'Open',
                          onClick: () => navigate(`/contacts/${c.id}`),
                        },
                        { icon: 'edit', label: 'Edit', onClick: () => setEditing(c) },
                        'divider',
                        {
                          icon: 'trash',
                          label: 'Delete',
                          onClick: () => setDeleting(c),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className="card-header"
            style={{
              justifyContent: 'space-between',
              borderTop: '1px solid var(--border)',
              borderBottom: 'none',
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              {total === 0 ? '0' : `${offset + 1}–${end}`} of {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button disabled={end >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <ContactFormModal
          row={editing === 'new' ? undefined : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            if (editing === 'new') {
              await createMut.mutateAsync(payload);
            } else {
              await updateMut.mutateAsync({ id: editing.id, body: payload });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete contact"
        message={
          deleting ? (
            <>
              Delete <strong>{deleting.full_name ?? 'this contact'}</strong>? This cannot be undone.
            </>
          ) : null
        }
        destructive
        confirmLabel="Delete"
        loading={deleteMut.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteMut.mutateAsync(deleting.id);
            setDeleting(null);
          } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}
